import { objectKeysToCamelCase, objectKeysToSnakeCase } from 'utils/common';
import { HistoricalPerDiemCalendarPeriod } from 'pages/Shipments/HistoricalPerDiem/params';
import axiosWithToken from './axios-with-token';

export enum InsightsQueryKeys {
  OutstandingFees = 'outstandingFees',
  OutstandingFeesBreakdown = 'outstandingFeesBreakdown',
  OutstandingFeesKeyMetrics = 'outstandingFeesKeyMetrics',
}

export enum OutstandingFeesType {
  PerDiem = 'per_diem',
  Demurrage = 'demurrage',
}

export interface OutstandingDetail {
  amount: number;
  numberOfContainers: number;
}

export interface OutstandingDemurrageDetail extends OutstandingDetail {
  averageDays: number | null;
  averageAmount: number | null;
}

export interface OutstandingPerDiemDetail extends OutstandingDetail {
  averageDays: number | null;
  averageAmount: number | null;
  disputeOpportunity: number | null;
  estimatedSavings: number | null;
}

export interface OutstandingFees {
  perDiem: OutstandingPerDiemDetail;
  demurrage: OutstandingDemurrageDetail;
}

export async function fetchOutstandingFees(signal?: AbortSignal): Promise<OutstandingFees> {
  const { data } = await axiosWithToken.get<OutstandingFees>(
    '/core/api/v1/insights/outstanding_fees',
    { signal }
  );
  return objectKeysToCamelCase(data);
}

interface OutstandingFeesParams {
  type: OutstandingFeesType;
  breakdown?: OutstandingFeesBreakdown;
  startDate?: string;
  endDate?: string;
  calendarPeriod?: HistoricalPerDiemCalendarPeriod;
}

interface OutstandingFeesKeyMetrics {
  totalContainers: number;
  totalAccrued: number;
  disputeOpportunity: number;
  estimatedSavings: number;
  avgPerDiem: number;
  avgDaysOfPerDiem: number;
}

export async function fetchOutstandingFeesKeyMetrics(
  params: OutstandingFeesParams,
  signal?: AbortSignal
): Promise<OutstandingFeesKeyMetrics> {
  const newParams: OutstandingFeesParams = params;
  if (newParams.breakdown) {
    delete newParams.breakdown;
  }
  const { data } = await axiosWithToken.get<OutstandingFeesKeyMetrics>(
    '/core/api/v1/insights/outstanding_fees/key-metrics/',
    { signal, params: objectKeysToSnakeCase(newParams) }
  );
  return objectKeysToCamelCase(data);
}

export interface BreakdownElement {
  label: string;
  key: string;
  amount: number;
  disputableAmount: number;
  numberOfContainers: number;
  accrualDate: string | null;
  daysCharged: number;
}

export enum OutstandingFeesBreakdown {
  Terminal = 'terminal',
  Port = 'port',
  ShippingLine = 'shipping_line',
  Container = 'transaction',
  OceanBOL = 'ocean_bol',
}

export interface OutstandingFeesBreakdownParams {
  type: OutstandingFeesType;
  breakdown?: OutstandingFeesBreakdown;
  startDate?: string;
  endDate?: string;
  calendarPeriod?: HistoricalPerDiemCalendarPeriod;
}

export async function fetchOutstandingFeesBreakdown(
  params: OutstandingFeesBreakdownParams,
  signal?: AbortSignal
): Promise<BreakdownElement[]> {
  const { data } = await axiosWithToken.get<BreakdownElement[]>(
    `/core/api/v1/insights/outstanding_fees/breakdown`,
    { signal, params: objectKeysToSnakeCase(params) }
  );

  return objectKeysToCamelCase(data);
}

interface ExportOutstandingFeesBreakdownResponse {
  exportLink: string;
}

export async function exportOutstandingFeesBreakdown(
  params: OutstandingFeesBreakdownParams,
  signal?: AbortSignal
): Promise<ExportOutstandingFeesBreakdownResponse> {
  const { data } = await axiosWithToken.get<ExportOutstandingFeesBreakdownResponse>(
    `/core/api/v1/insights/outstanding_fees/breakdown/export`,
    { signal, params: objectKeysToSnakeCase(params) }
  );
  return objectKeysToCamelCase(data);
}
